/**
 * Generation pipeline for "Tela em branco" flows: plan → keyframes → multiclip video → join.
 * Media always travels as gallery/CDN URLs (never base64 in job payloads).
 */

import { supabase } from './supabase';
import { isCdnUploadEnabled, toMediaUrl } from './mediaUrl';
import { uploadToGallery } from './mediaGallery';
import { uploadViaCdn } from './uploadMedia';
import { composeOutpaint, cropStillToRatio, hasAspect, hasBakedBars, renderOutpaintBase, thumbnailBase64 } from './stillNormalize';
import { calibratedAspectFromUi, letterboxStillToAspect, modelNeedsCalibratedStills } from './calibratedStill';
import {
  estimateCredits,
  getVideoModelById,
  maxReferenceImages,
  rateForGeneratedAudio,
  supportsGeneratedAudio,
  type VideoModelOption,
} from './videoModelCatalog';
import {
  buildKeyframeIdentityClause,
  chunkPhasesForModel,
  collectPhaseImageRefs,
  compileCreateVideoBodyForChunk,
  createEmptyPhase,
  mergePlanOntoPhases,
  retimePhases,
  type CreateVideoCompileResult,
  type FragmentadorSession,
  type FragmentadorSessionPhase,
  type FragmentadorVideoChunk,
  type SessionRequestPlan,
} from './fragmentadorCompile';
import {
  buildStillPrompt,
  clampPhaseCount,
  type FragmentadorPlan,
} from './fragmentadorPromptContract';
import {
  jobIdsFromAsyncResponse,
  jobStatusLabel,
  rememberCreationVideoJob,
  suppressVideoJobCompletionToast,
  waitForJob,
} from './jobsApi';
import type { VideoModelPickerValue } from '../components/video/VideoModelPicker';
import { ASPECT_RATIOS, fitRequests, newId, type BoardAspect, type BoardImageMode, type BoardVideoNode } from './canvasBoard';

export async function blobOrDataToFile(source: Blob | string, name: string): Promise<File> {
  if (typeof source !== 'string') {
    return source instanceof File ? source : new File([source], name, { type: source.type || 'image/jpeg' });
  }
  const res = await fetch(source);
  if (!res.ok) throw new Error(`Falha ao baixar imagem (${res.status}).`);
  const blob = await res.blob();
  const ext = (blob.type || '').includes('png') ? 'png' : 'jpg';
  return new File([blob], `${name}.${ext}`, { type: blob.type || 'image/jpeg' });
}

/** Letterbox only for H3 Max; other models keep the source as-is. */
export async function uploadCanvasStill(
  orgId: string,
  source: Blob | string,
  opts: { aspect: string; title: string; videoModelId?: string | null; existingUrl?: string | null },
): Promise<string> {
  if (modelNeedsCalibratedStills(opts.videoModelId)) {
    const { file } = await letterboxStillToAspect(source, calibratedAspectFromUi(opts.aspect));
    const asset = await uploadToGallery(orgId, file, { title: opts.title });
    return toMediaUrl(asset.video_url);
  }
  const existing = opts.existingUrl || (typeof source === 'string' && /^https?:\/\//i.test(source) ? source : null);
  if (existing) return toMediaUrl(existing);
  const file = await blobOrDataToFile(source, `tela-${Date.now()}`);
  const asset = await uploadToGallery(orgId, file, { title: opts.title });
  return toMediaUrl(asset.video_url);
}

/** Prefer the JSON `{ error }` body over the generic non-2xx message from supabase-js. */
export async function edgeErrorMessage(
  error: { message?: string; context?: Response } | null,
  data: { error?: unknown } | null | undefined,
): Promise<string> {
  if (data && typeof data === 'object' && data.error != null) return String(data.error);
  const ctx = error?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await ctx.json();
      if (body?.error) return String(body.error);
      if (body?.message) return String(body.message);
    } catch {
      /* body already consumed or not JSON */
    }
  }
  return error?.message || 'Falha na edge function.';
}

export function isCreditError(msg: string): boolean {
  const m = msg.toLowerCase();
  // "Nenhum crédito foi cobrado" means the debit did not happen, not that the balance is empty.
  if (m.includes('nenhum crédito') || m.includes('nada foi debitado')) return false;
  return m.includes('not enough credits')
    || m.includes('créditos insuficientes')
    || m.includes('insufficient credits');
}

export type PlannedFlow = {
  phases: FragmentadorSessionPhase[];
  look: FragmentadorPlan['look'];
  intentHint: FragmentadorPlan['intent_hint'];
  totalDurationSeconds: number;
  requestPlan: SessionRequestPlan;
  /** Requests the planner could not place in the storyboard. */
  uncovered: string[];
};

/** Where new keyframes go relative to the user's photos. */
export type NewScenesPosition = 'between' | 'before' | 'after';

/** 0-based scene index of each user photo in a flow of `count` scenes. */
export function userImageSlots(count: number, k: number, position: NewScenesPosition): number[] {
  if (position === 'before') return Array.from({ length: k }, (_, i) => count - k + i);
  if (position === 'after' || k <= 1) return Array.from({ length: k }, (_, i) => i);
  return Array.from({ length: k }, (_, i) => Math.round((i * (count - 1)) / (k - 1)));
}

/**
 * Keep every user photo in the flow. With `force`, photos always land on `slots`;
 * otherwise only when the planner forgot to reuse one.
 */
function pinUserImages(
  merged: FragmentadorSessionPhase[],
  existing: FragmentadorSessionPhase[],
  slots: number[],
  force: boolean,
): FragmentadorSessionPhase[] {
  const userUrls = new Set(existing.map((p) => p.imageUrl).filter(Boolean) as string[]);
  const present = new Set(merged.map((p) => p.imageUrl).filter(Boolean) as string[]);
  const allPresent = Array.from(userUrls).every((u) => present.has(u));
  let out = merged;
  if (force || !allPresent) {
    out = merged.map((p) =>
      p.imageUrl && userUrls.has(p.imageUrl)
        ? { ...p, imageUrl: undefined, status: 'pending' as const, jobId: undefined }
        : p,
    );
    existing.forEach((e, i) => {
      const slot = slots[i] ?? i;
      out[slot] = { ...out[slot], id: e.id, imageUrl: e.imageUrl, status: 'ready', jobId: e.jobId };
    });
  }
  const seen = new Set<string>();
  const keptIds = new Set(existing.map((e) => e.id));
  return out.map((p) => {
    const fresh = !p.imageUrl && keptIds.has(p.id);
    if (seen.has(p.id) || fresh || !p.id) {
      const id = newId('scene');
      const repeatsPhoto = seen.has(p.id) && Boolean(p.imageUrl);
      seen.add(id);
      return repeatsPhoto
        ? { ...p, id, imageUrl: undefined, status: 'pending' as const, jobId: undefined }
        : { ...p, id };
    }
    seen.add(p.id);
    return p;
  });
}

type PlanReferenceImage = { label: string; mime_type: 'image/jpeg'; data: string };

/** Every user photo, downscaled, labeled with the scene it occupies — so the planner sees mids too. */
async function planReferenceImages(
  images: FragmentadorSessionPhase[],
  slots: number[],
): Promise<PlanReferenceImage[]> {
  const out = await Promise.all(
    images.map(async (p, i): Promise<PlanReferenceImage | null> => {
      if (!p.imageUrl) return null;
      try {
        const data = await thumbnailBase64(p.imageUrl);
        if (!data) return null;
        const scene = (slots[i] ?? i) + 1;
        return { label: `SCENE ${scene} keyframe photo — cite as <<<image_${scene}>>>:`, mime_type: 'image/jpeg', data };
      } catch {
        return null;
      }
    }),
  );
  return out.filter((r): r is PlanReferenceImage => Boolean(r));
}

export async function planFlow(args: {
  orgId: string;
  brief: string;
  /** Every request the user made for this video, oldest first (defaults to `brief`). */
  requests?: string[];
  images: FragmentadorSessionPhase[];
  keyframes: boolean;
  sceneCount: number;
  aspect: BoardAspect;
  /** Total the user asked for; locks the planner and the final timing. */
  durationSeconds?: number;
  /** Where new keyframes go; default = between photos (or after a single photo). */
  newScenesPosition?: NewScenesPosition;
  /**
   * Rewrite an existing script for the images as they are now (arrow order, edits).
   * Drops the previous scene prompts so the planner follows the photos, not the old text.
   */
  rewrite?: boolean;
}): Promise<PlannedFlow> {
  const { orgId, brief, images, keyframes, aspect, durationSeconds } = args;
  const count = clampPhaseCount(keyframes ? Math.max(args.sceneCount, images.length) : images.length);
  const newBeats = Math.max(0, count - images.length);
  const position: NewScenesPosition = newBeats ? args.newScenesPosition || 'between' : 'between';
  const slots = userImageSlots(count, images.length, position);
  const slotList = slots
    .map((s, i) => `cena ${s + 1} = foto ${i + 1} (reuse_from_index ${i + 1}; no prompt cite <<<image_${s + 1}>>>)`)
    .join('; ');
  const rewriteNote = args.rewrite
    ? ' A ordem abaixo é a ordem atual das setas, escolhida pelo usuário, e as fotos podem ter sido editadas depois do roteiro anterior. Reescreva cada cena para o que a foto dessa posição mostra agora.'
    : '';
  const instruction = !keyframes
    ? `Cada imagem atual é exatamente uma cena, na mesma ordem (reuse_from_index = índice da imagem). Não crie cenas novas: ${count} cenas no total.`
    : position === 'before'
    ? `As ${images.length} imagens atuais são fotos do usuário e são o DESTINO do vídeo: ${slotList}. Crie ${newBeats} cena(s) nova(s) (reuse_from_index null) ANTES delas, nas cenas 1 a ${newBeats}, que preparem e levem naturalmente até a primeira foto. ${count} cenas no total.`
    : position === 'after'
    ? `As ${images.length} imagens atuais são fotos do usuário e abrem o vídeo: ${slotList}. Crie ${newBeats} cena(s) nova(s) (reuse_from_index null) DEPOIS delas, continuando a partir da última foto. ${count} cenas no total.`
    : `As ${images.length} imagens atuais são fotos do usuário e keyframes obrigatórias, nesta ordem: ${slotList}. Crie ${newBeats} cena(s) intermediária(s) nova(s) (reuse_from_index null) nas demais posições para completar ${count} cenas.`;
  const structure = `${instruction}${rewriteNote}`;

  const hasStart = slots[0] === 0;
  const hasEnd = count > 1 && slots[slots.length - 1] === count - 1;
  const referenceImages = await planReferenceImages(images, slots);
  const fallbackBrief = brief.trim() || 'Vídeo contínuo e cinematográfico com base nas imagens do usuário.';
  const userRequests = fitRequests(args.requests?.length ? args.requests : [fallbackBrief]);

  const { data, error } = await supabase.functions.invoke('fragmentador-plan', {
    body: {
      brief: fallbackBrief,
      user_requests: userRequests,
      structure_instruction: structure,
      current_phases: images.map((p, i) => ({
        id: p.id,
        label: p.label,
        role: slots[i] === 0 ? 'start' : slots[i] === count - 1 ? 'end' : 'beat',
        description_pt: args.rewrite ? '' : p.description_pt,
        image_index: i + 1,
        status: 'ready',
        has_image: true,
        ...(!args.rewrite && p.video_prompt_en
          ? {
              camera_move: p.camera_move,
              transition_out: p.transition_out,
              pace: p.pace,
              energy: p.energy,
              duration_seconds: Math.max(1, Math.round(Number(p.t_end) - Number(p.t_start)) || 0) || undefined,
              video_prompt_en: p.video_prompt_en,
              ...(p.action_en ? { action_en: p.action_en } : {}),
              ...(p.on_screen_text?.length ? { on_screen_text: p.on_screen_text } : {}),
            }
          : {}),
      })),
      phase_count_target: count,
      organization_id: orgId,
      duration_seconds: durationSeconds || count * 2,
      duration_min: durationSeconds || 4,
      duration_max: durationSeconds || 30,
      aspect_ratio: aspect,
      has_start: hasStart,
      has_end: hasEnd,
      start_url: hasStart ? images[0]?.imageUrl : undefined,
      end_url: hasEnd ? images[images.length - 1]?.imageUrl : undefined,
      ...(referenceImages.length ? { reference_images: referenceImages } : {}),
    },
  });
  if (error || data?.error) throw new Error(await edgeErrorMessage(error, data));
  const plan = data.plan as FragmentadorPlan;
  if (!plan?.phases?.length) throw new Error('O planejador não retornou cenas.');
  if (plan.phases.length < images.length) {
    throw new Error('O planejador devolveu menos cenas que imagens. Tente de novo.');
  }
  const merged = pinUserImages(mergePlanOntoPhases(plan, images), images, slots, position !== 'between');
  const timed = retimePhases(merged, durationSeconds || plan.total_duration_seconds);
  return {
    phases: timed.phases,
    look: plan.look,
    intentHint: plan.intent_hint,
    totalDurationSeconds: timed.totalDurationSeconds,
    requestPlan: {
      requirements: plan.requirements,
      styleEn: plan.style_en,
      continuityEn: plan.continuity_en,
      avoidEn: plan.avoid_en,
      allow: plan.allow,
    },
    uncovered: Array.isArray(plan.uncovered) ? plan.uncovered.map(String).filter(Boolean) : [],
  };
}

export async function generateStill(args: {
  orgId: string;
  phases: FragmentadorSessionPhase[];
  index: number;
  imageModelId: string;
  aspect: BoardAspect;
  intentHint?: FragmentadorPlan['intent_hint'];
  videoModelId?: string;
  onJob?: (jobId: string) => void;
  onStatus?: (label: string) => void;
}): Promise<{ url: string; jobId: string }> {
  const { orgId, phases, index } = args;
  const phase = phases[index];
  const startUrl = phases[0]?.imageUrl;
  const endUrl = phases.length > 1 ? phases[phases.length - 1]?.imageUrl : undefined;
  const refs = collectPhaseImageRefs(phases, index, startUrl, endUrl);
  const refUrls = refs.map((r) => r.url);
  const refUrl = refUrls[0];
  const prompt =
    buildStillPrompt(phase) ||
    'Photoreal cinematic still matching the reference identity, materials and lighting. No sketch, no illustration.';
  const identity = buildKeyframeIdentityClause(refs, index, phases.length, args.intentHint);

  const { url: raw, jobId } = await runImageJob(
    {
      organization_id: orgId,
      prompt: `${prompt}${identity}`.replace(/\s+/g, ' ').trim(),
      image_model_id: args.imageModelId,
      aspect_ratio: args.aspect,
      single_image: true,
      ...(refUrl ? { image_url: refUrl } : {}),
      ...(refUrls.length > 1 ? { image_urls: refUrls } : {}),
      phase_description: phase.description_pt,
    },
    args,
  );
  const url = await uploadCanvasStill(orgId, raw, {
    aspect: args.aspect,
    title: `Tela em branco · ${phase.label || 'cena'}`,
    videoModelId: args.videoModelId,
    existingUrl: raw,
  });
  return { url, jobId };
}

/** Picks up a still whose job was already queued before the page was left (no new request, no new charge). */
export async function resumeStillJob(args: {
  orgId: string;
  jobId: string;
  aspect: BoardAspect;
  title: string;
  videoModelId?: string;
  onStatus?: (label: string) => void;
}): Promise<string> {
  const job = await waitForJob(args.jobId, {
    timeoutMs: 15 * 60 * 1000,
    onStatus: (j) => {
      if (j.status !== 'completed' && j.status !== 'failed') args.onStatus?.(jobStatusLabel(j));
    },
  });
  const raw = toMediaUrl(job.result_url);
  if (!raw) throw new Error('Geração concluída sem imagem.');
  return uploadCanvasStill(args.orgId, raw, { aspect: args.aspect, title: args.title, videoModelId: args.videoModelId, existingUrl: raw });
}

/** Text-to-image still for a new board node. */
export async function generateImageFromPrompt(args: {
  orgId: string;
  prompt: string;
  imageModelId: string;
  aspect: BoardAspect;
  title: string;
  onJob?: (jobId: string) => void;
  onStatus?: (label: string) => void;
}): Promise<{ url: string; jobId: string }> {
  const { url: raw, jobId } = await runImageJob(
    {
      organization_id: args.orgId,
      prompt: args.prompt.trim(),
      image_model_id: args.imageModelId,
      aspect_ratio: args.aspect,
      single_image: true,
    },
    args,
  );
  const url = await uploadCanvasStill(args.orgId, raw, { aspect: args.aspect, title: args.title, existingUrl: raw });
  return { url, jobId };
}

/** Image-to-image edit of a board still: only what `instruction` asks changes. */
export async function editStill(args: {
  orgId: string;
  sourceUrl: string;
  instruction: string;
  imageModelId: string;
  aspect: BoardAspect;
  title: string;
  /** Extra plates (e.g. room photo for background replace); first input stays `sourceUrl`. */
  referenceUrls?: string[];
  /**
   * Compose sends `instruction` as the whole prompt. In-place edits stay wrapped so only
   * the asked change happens.
   */
  verbatimPrompt?: boolean;
  onJob?: (jobId: string) => void;
  onStatus?: (label: string) => void;
}): Promise<{ url: string; jobId: string }> {
  const refs = (args.referenceUrls || []).filter((u) => u && u !== args.sourceUrl);
  const prompt = args.verbatimPrompt
    ? args.instruction.trim()
    : refs.length
    ? `Edit the first input photo. Change only this: ${args.instruction.trim()} ` +
      'Use the additional reference image(s) only as visual base for that change (style, environment, object, lighting — as requested). ' +
      'Do not invent the reference content from text; match what those reference images show. ' +
      'Keep the first photo\'s subjects, framing, composition and perspective unless the instruction says otherwise. Photoreal, seamless, no borders, no watermark.'
    : `Edit the input photo. Change only this: ${args.instruction.trim()} ` +
      'Keep everything else exactly as in the input: same framing, composition, perspective, subjects, materials, ' +
      'lighting and colors. Photoreal, seamless, no borders, no watermark.';
  const imageUrls = refs.length ? [args.sourceUrl, ...refs] : undefined;
  const { url: raw, jobId } = await runImageJob(
    {
      organization_id: args.orgId,
      prompt: prompt.replace(/\s+/g, ' ').trim(),
      image_model_id: args.imageModelId,
      aspect_ratio: args.aspect,
      single_image: true,
      image_url: args.sourceUrl,
      ...(imageUrls ? { image_urls: imageUrls } : {}),
    },
    args,
  );
  // The model sometimes returns another shape. When the caller asked for a ratio, the saved
  // file keeps that ratio (center crop) instead of a letterboxed canvas.
  const matches = await hasAspect(raw, args.aspect).catch(() => false);
  if (matches) {
    const url = await uploadCanvasStill(args.orgId, raw, { aspect: args.aspect, title: args.title, existingUrl: raw });
    return { url, jobId };
  }
  const fitted = await cropStillToRatio(raw, ASPECT_RATIOS[args.aspect]);
  const url = await uploadCanvasStill(args.orgId, fitted.file, { aspect: args.aspect, title: args.title });
  return { url, jobId };
}

/**
 * Redraw the photo's scene in another aspect (the edit model fills the new area itself, no gray canvas).
 * Unlike `outpaintStill` the whole picture is regenerated, so details may drift.
 */
export async function recomposeStill(args: {
  orgId: string;
  sourceUrl: string;
  aspect: BoardAspect;
  imageModelId: string;
  title: string;
  onJob?: (jobId: string) => void;
  onStatus?: (label: string) => void;
}): Promise<{ url: string; jobId: string }> {
  const r = ASPECT_RATIOS[args.aspect];
  const shape = r > 1.05 ? 'horizontal' : r < 0.95 ? 'vertical' : 'square';
  const prompt =
    `Recompose this exact scene as a ${shape} ${args.aspect} image. ` +
    'Keep the same subjects, architecture, materials, facade colors, proportions, lighting, sky and color grading exactly as in the input photo. ' +
    'Extend the surroundings naturally to fill the new frame (more sky, ground or foreground as needed); ' +
    'do not stretch, squash or distort anything. Photoreal, seamless, no borders, no added text, no watermark.';
  const { url: raw, jobId } = await runImageJob(
    {
      organization_id: args.orgId,
      prompt,
      image_model_id: args.imageModelId,
      aspect_ratio: args.aspect,
      single_image: true,
      image_url: args.sourceUrl,
    },
    args,
  );
  if (!(await hasAspect(raw, args.aspect))) {
    throw new Error(`O modelo devolveu a imagem em outra proporção (pedido ${args.aspect}). Tente outro modelo para recompor.`);
  }
  const url = await uploadCanvasStill(args.orgId, raw, { aspect: args.aspect, title: args.title, existingUrl: raw });
  return { url, jobId };
}

/** create-preview single image: async job (PoYo / queue) or sync Gemini preview URL. */
async function runImageJob(
  body: Record<string, unknown>,
  cb: { onJob?: (jobId: string) => void; onStatus?: (label: string) => void },
): Promise<{ url: string; jobId: string }> {
  const { data, error } = await supabase.functions.invoke('create-preview', { body });
  if (error || data?.error) throw new Error(await edgeErrorMessage(error, data));
  const jobId = data?.job_id || jobIdsFromAsyncResponse(data)[0] || '';
  if (!jobId) {
    const url = toMediaUrl(data?.previews?.[0]?.image_b64);
    if (!url) throw new Error('create-preview não retornou imagem nem job_id.');
    return { url, jobId: '' };
  }
  cb.onJob?.(jobId);
  const job = await waitForJob(jobId, {
    timeoutMs: 15 * 60 * 1000,
    onStatus: (j) => {
      if (j.status !== 'completed' && j.status !== 'failed') cb.onStatus?.(jobStatusLabel(j));
    },
  });
  const url = toMediaUrl(job.result_url);
  if (!url) throw new Error('Geração concluída sem imagem.');
  return { url, jobId };
}

const OUTPAINT_PROMPT =
  'Outpainting. The input is a real photo centered on a flat gray (#808080) canvas. ' +
  'Replace every gray area by naturally extending the same scene outward: continue the architecture, ' +
  'ground, sky, materials, perspective, lighting, shadows and color grading seamlessly. ' +
  'Keep the central photo exactly as it is (same framing, position, scale and content). ' +
  'Photoreal. No gray bars, no borders, no frames, no text, no watermark.';

async function uploadIntermediate(orgId: string, file: File): Promise<string> {
  if (!isCdnUploadEnabled()) {
    throw new Error('Upload de mídia indisponível: CDN/S3 desligado neste ambiente.');
  }
  const { publicUrl } = await uploadViaCdn({ organizationId: orgId, file, purpose: 'frame', filename: file.name });
  return toMediaUrl(publicUrl);
}

/** Expand a still to `frame` with AI; original pixels stay untouched in the center. */
export async function outpaintStill(args: {
  orgId: string;
  sourceUrl: string;
  frame: { width: number; height: number };
  imageModelId: string;
  title: string;
  onJob?: (jobId: string) => void;
  onStatus?: (label: string) => void;
}): Promise<{ url: string; width: number; height: number }> {
  const { orgId, sourceUrl, frame } = args;
  const base = await renderOutpaintBase(sourceUrl, frame);
  const baseUrl = await uploadIntermediate(orgId, base.file);
  const { url: aiUrl } = await runImageJob(
    {
      organization_id: orgId,
      prompt: OUTPAINT_PROMPT,
      image_model_id: args.imageModelId,
      aspect_ratio: base.aspect,
      single_image: true,
      image_url: baseUrl,
    },
    args,
  );
  if (!(await hasAspect(aiUrl, base.aspect))) {
    throw new Error(`O modelo de expansão devolveu a imagem em outra proporção (pedido ${base.aspect}). Tente outro modelo de expansão.`);
  }
  if (await hasBakedBars(aiUrl)) {
    throw new Error('O modelo de imagem devolveu as bordas cinza sem expandir o cenário. Tente outro modelo de expansão ou recorte a foto.');
  }
  args.onStatus?.('Compondo…');
  const out = await composeOutpaint(sourceUrl, aiUrl, frame, base.aspect);
  const asset = await uploadToGallery(orgId, out.file, { title: args.title });
  return { url: toMediaUrl(asset.video_url), width: out.width, height: out.height };
}

export function flowChunks(phases: FragmentadorSessionPhase[], videoModelId: string): FragmentadorVideoChunk[] {
  const model = getVideoModelById(videoModelId);
  if (!model || !phases.length) return [];
  return chunkPhasesForModel(phases, {
    maxReferenceImages: maxReferenceImages(model),
    durationMax: model.durationMax || 15,
    durationMin: model.durationMin,
    startUrl: phases[0]?.imageUrl,
    endUrl: phases[phases.length - 1]?.imageUrl,
  });
}

/**
 * Scene ranges that become separate flows on the board, each one video call: split at hard cuts and
 * wherever the plan is longer, or has more images, than the model takes in one video.
 */
export function flowSegments(phases: FragmentadorSessionPhase[], videoModelId: string): Array<{ start: number; end: number }> {
  const chunks = flowChunks(phases, videoModelId);
  if (!chunks.length) return phases.length ? [{ start: 0, end: phases.length }] : [];
  return chunks.map((c) => ({ start: c.startIndex, end: c.endIndex }));
}

/**
 * One create-video call of a flow.
 * clip: keyframes, the scenes play in arrow order (2 images = exact first/last frame; more = ordered references).
 * still: one scene only.
 * group: reference mode, the scenes go together as visual references with no position in time.
 */
export type FlowPart = {
  kind: 'clip' | 'still' | 'group';
  startIndex: number;
  /** Exclusive. */
  endIndex: number;
  plannedSeconds: number;
  /** Seconds sent to the model (clamped to its min/max). */
  durationSeconds: number;
  chunk: FragmentadorVideoChunk;
};

function modelSeconds(model: VideoModelOption, planned: number): number {
  const d = Math.max(model.durationMin || 1, Math.min(model.durationMax || 15, Math.round(planned)));
  // Kling Omni only renders 5 or 10 s.
  if (model.provider === 'kling') return d <= 5 ? 5 : 10;
  return d;
}

export function flowParts(
  phases: FragmentadorSessionPhase[],
  videoModelId: string,
  mode: BoardImageMode = 'keyframes',
): FlowPart[] {
  const model = getVideoModelById(videoModelId);
  if (!model || !phases.length) return [];
  return flowChunks(phases, videoModelId).map((c) => ({
    kind: c.endIndex - c.startIndex === 1 ? 'still' : mode === 'reference' ? 'group' : 'clip',
    startIndex: c.startIndex,
    endIndex: c.endIndex,
    plannedSeconds: c.durationSeconds,
    durationSeconds: modelSeconds(model, c.durationSeconds),
    chunk: c,
  }));
}

/** How each scene image acts in the video, for the label on its node. */
export type SceneImageRole = 'start' | 'end' | 'keyframe' | 'reference';

export function sceneImageRoles(parts: FlowPart[], sceneCount: number): SceneImageRole[] {
  const starts = new Array(sceneCount).fill(false);
  const ends = new Array(sceneCount).fill(false);
  const refs = new Array(sceneCount).fill(false);
  const mids = new Array(sceneCount).fill(false);
  for (const p of parts) {
    if (p.kind === 'group') {
      for (let i = p.startIndex; i < p.endIndex; i++) refs[i] = true;
      continue;
    }
    starts[p.startIndex] = true;
    if (p.endIndex - p.startIndex > 1) ends[p.endIndex - 1] = true;
    for (let i = p.startIndex + 1; i < p.endIndex - 1; i++) mids[i] = true;
  }
  return starts.map((s, i) => (refs[i] ? 'reference' : mids[i] ? 'keyframe' : ends[i] ? 'end' : 'start'));
}

function compileFlowPart(
  orgId: string,
  session: FragmentadorSession,
  parts: FlowPart[],
  i: number,
  imageMode: BoardImageMode = 'keyframes',
) {
  const prev = parts[i - 1]?.chunk;
  return compileCreateVideoBodyForChunk({
    organizationId: orgId,
    session,
    chunk: parts[i].chunk,
    prevChunkLastCamera: prev ? session.phases[prev.endIndex - 1]?.camera_move : undefined,
    partIndex: i,
    partCount: parts.length,
    lockOpening: imageMode !== 'reference',
  });
}

/** Why a plan does not fit one video of this model, or null when it does. */
export function flowSplitReason(phases: FragmentadorSessionPhase[], videoModelId: string): string | null {
  const model = getVideoModelById(videoModelId);
  if (!model || phases.length < 2) return null;
  const chunks = flowChunks(phases, videoModelId);
  if (chunks.length < 2) return null;
  const seconds = phases[phases.length - 1]?.t_end || 0;
  const images = new Set(phases.map((p) => p.imageUrl).filter(Boolean)).size;
  const maxSeconds = model.durationMax || 15;
  const maxImages = Math.max(2, maxReferenceImages(model));
  const why = [
    seconds > maxSeconds ? `${seconds}s de roteiro e o ${model.label} gera até ${maxSeconds}s por vídeo` : '',
    images > maxImages ? `${images} imagens e o ${model.label} aceita ${maxImages} por vídeo` : '',
  ].filter(Boolean);
  const cuts = phases.some((p, i) => i < phases.length - 1 && p.transition_out === 'cut');
  if (!why.length) return cuts ? 'cada corte seco vira um trecho' : null;
  return why.join(' · ');
}

/** Shift a slice of the plan so its timeline starts at 0. */
export function rebasePhases(phases: FragmentadorSessionPhase[]): FragmentadorSessionPhase[] {
  const offset = phases[0]?.t_start || 0;
  if (!offset) return phases;
  return phases.map((p) => ({ ...p, t_start: p.t_start - offset, t_end: p.t_end - offset }));
}

/** Still phases from the generator card, when the node has no linked image chain. */
export function phasesFromGenerator(node: BoardVideoNode, durationSeconds: number): FragmentadorSessionPhase[] {
  const g = node.generator;
  const items: Array<{ url: string; label: string }> = [];
  if (g?.startImage?.url) items.push({ url: g.startImage.url, label: 'Início' });
  (g?.attachments || []).forEach((a, i) => {
    if (a.url) items.push({ url: a.url, label: a.label || `Anexo ${i + 1}` });
  });
  if (g?.endImage?.url) items.push({ url: g.endImage.url, label: 'Fim' });
  if (!items.length) return [];
  const total = Math.max(items.length, Math.round(durationSeconds) || items.length * 2);
  const prompt = (g?.prompt || node.brief || '').trim();
  let t = 0;
  return items.map((item, i) => {
    const remaining = items.length - i;
    const span = Math.max(1, Math.round((total - t) / remaining));
    const tEnd = i === items.length - 1 ? total : t + span;
    const phase = createEmptyPhase(i);
    const next: FragmentadorSessionPhase = {
      ...phase,
      label: item.label,
      role: i === 0 ? 'start' : i === items.length - 1 ? 'end' : 'beat',
      description_pt: prompt || item.label,
      video_prompt_en: prompt || phase.video_prompt_en,
      still_prompt_en: prompt || phase.still_prompt_en,
      t_start: t,
      t_end: Math.max(t + 1, tEnd),
      status: 'ready',
      imageUrl: item.url,
      image_index: i + 1,
      transition_out: 'camera',
    };
    t = next.t_end;
    return next;
  });
}

export function estimateFlowVideoCredits(
  phases: FragmentadorSessionPhase[],
  pick: VideoModelPickerValue,
  mode: BoardImageMode = 'keyframes',
): number {
  const model = getVideoModelById(pick.videoModelId);
  if (!model) return 0;
  const resolution = model.resolutions.find((r) => r.id === pick.resolutionId) || model.resolutions[0];
  if (!resolution) return 0;
  const rate = rateForGeneratedAudio(resolution, Boolean(pick.generateAudio) && supportsGeneratedAudio(model));
  const planned = flowParts(phases, model.id, mode);
  const total = phases[phases.length - 1]?.t_end || pick.durationSeconds;
  const parts = planned.length ? planned : [{ durationSeconds: total }];
  return parts.reduce(
    (sum, c) => sum + estimateCredits({ creditsPerSecond: rate.creditsPerSecond, durationSeconds: c.durationSeconds }),
    0,
  );
}

export type Stitcher = (urls: string[], title: string) => Promise<string>;

export async function generateFlowVideo(args: {
  orgId: string;
  session: FragmentadorSession;
  pick: VideoModelPickerValue;
  stitch: Stitcher;
  galleryTitle?: string;
  imageMode?: BoardImageMode;
  existingPartUrls?: string[];
  /** Jobs already queued for each part (page reopened mid-generation): waited on instead of requested again. */
  existingPartJobIds?: string[];
  /** Text typed on the generator card, placed in front of the compiled prompt. */
  userPrompt?: string;
  /** Contínuo: liga a foto de abertura ao vídeo com o minterpolate curto. */
  openBridge?: (stillUrl: string, videoUrl: string) => Promise<string>;
  referenceVideoUrl?: string;
  audioUrl?: string;
  onProgress?: (p: { label: string; partUrls: string[]; partJobIds: string[] }) => void;
}): Promise<{ clipUrl: string; partUrls: string[]; partJobIds: string[]; parts: number; prompt: string }> {
  const { orgId, session, pick } = args;
  const chunks = flowParts(session.phases, pick.videoModelId, args.imageMode);
  if (!chunks.length) throw new Error('Nenhuma cena para gerar.');
  const imageMode = args.imageMode || 'keyframes';
  const compiledParts = chunks.map((_, ci) => compileFlowPart(orgId, session, chunks, ci, imageMode));
  for (const compiled of compiledParts) {
    if (compiled.issues.length) throw new Error(compiled.issues.map((i) => i.message).join(' · '));
  }
  const partPrompt = (compiled: CreateVideoCompileResult) => {
    const base = String(compiled.body.prompt || '');
    return args.userPrompt && !base.includes(args.userPrompt) ? `${args.userPrompt}\n${base}`.trim() : base;
  };
  const sentPrompt = joinPartPrompts(compiledParts.map(partPrompt));

  const resumable = args.existingPartUrls?.length === chunks.length ? args.existingPartUrls : undefined;
  const resumableJobs = args.existingPartJobIds?.length === chunks.length ? args.existingPartJobIds : undefined;
  const partUrls = chunks.map((_, i) => resumable?.[i] || '');
  const partJobIds = chunks.map((_, i) => (partUrls[i] ? '' : resumableJobs?.[i] || ''));
  const multi = chunks.length > 1;
  const progress = (label: string) =>
    args.onProgress?.({ label, partUrls: [...partUrls], partJobIds: [...partJobIds] });
  const doneLabel = () => `${partUrls.filter(Boolean).length}/${chunks.length} partes prontas`;

  // Parts only depend on the plan (not on each other's output), so they render in parallel.
  const runPart = async (i: number): Promise<void> => {
    const chunk = chunks[i];
    const label = multi ? `Parte ${i + 1}/${chunks.length}` : 'Gerando vídeo';
    progress(multi ? doneLabel() : label);
    const compiled = compiledParts[i];
    const queued = partJobIds[i];
    const { data, error } = queued
      ? { data: { async: true, job_id: queued }, error: null }
      : await supabase.functions.invoke('create-video', {
        body: {
          ...compiled.body,
          video_model_id: pick.videoModelId,
          resolution_id: pick.resolutionId,
          duration_seconds: chunk.durationSeconds,
          generate_audio: Boolean(pick.generateAudio),
          aspect_ratio: session.aspectRatio,
          ...(args.referenceVideoUrl ? { video_reference_url: args.referenceVideoUrl } : {}),
          ...(args.audioUrl ? { audio_url: args.audioUrl } : {}),
          prompt: partPrompt(compiled),
          gallery_title: args.galleryTitle || undefined,
          suppress_completion_notify: multi,
        },
      });
    if (error || data?.error) {
      const msg = await edgeErrorMessage(error, data);
      throw new Error(multi ? `${label}: ${msg}` : msg);
    }
    let url = '';
    if (data?.async && data.job_id) {
      if (multi) suppressVideoJobCompletionToast(data.job_id);
      else rememberCreationVideoJob(data.job_id);
      partJobIds[i] = data.job_id;
      progress(multi ? doneLabel() : label);
      const job = await waitForJob(data.job_id, {
        timeoutMs: 30 * 60 * 1000,
        onStatus: (j) => {
          if (j.status !== 'completed' && j.status !== 'failed') {
            progress(multi ? `${doneLabel()} · ${label} ${jobStatusLabel(j)}` : `${label} · ${jobStatusLabel(j)}`);
          }
        },
      });
      url = toMediaUrl(job.result_url) || '';
    } else if (data?.success && data.video_url) {
      url = toMediaUrl(data.video_url) || '';
    }
    if (!url) throw new Error(multi ? `${label}: sem URL de resultado.` : 'Vídeo sem URL de resultado.');
    const stillUrl = session.phases[chunk.startIndex]?.imageUrl;
    if (imageMode !== 'reference' && stillUrl && args.openBridge) {
      progress(multi ? `${doneLabel()} · ligando a imagem inicial` : 'Ligando a imagem inicial');
      try {
        url = await args.openBridge(stillUrl, url);
      } catch (err) {
        console.warn('opening bridge failed', err);
      }
    }
    partUrls[i] = url;
    progress(multi ? doneLabel() : label);
  };

  const results = await Promise.allSettled(chunks.map((_, i) => (partUrls[i] ? Promise.resolve() : runPart(i))));
  const failed = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (failed) throw failed.reason instanceof Error ? failed.reason : new Error(String(failed.reason));

  progress(multi ? 'Juntando partes' : 'Finalizando');
  const clipUrl = multi
    ? await args.stitch(partUrls, args.galleryTitle || `Tela em branco · ${session.phases.length} cenas`)
    : partUrls[0];
  return { clipUrl, partUrls, partJobIds, parts: chunks.length, prompt: sentPrompt };
}

function joinPartPrompts(prompts: string[]): string {
  if (prompts.length <= 1) return prompts[0] || '';
  return prompts.map((p, i) => `[Parte ${i + 1}/${prompts.length}] ${p}`).join('\n\n');
}

/** The full prompt the flow sends to create-video, as the generator card shows it. */
export function flowPromptText(session: FragmentadorSession, mode: BoardImageMode = 'keyframes'): string {
  const parts = flowParts(session.phases, session.videoModelId, mode);
  if (!parts.length) return '';
  return joinPartPrompts(parts.map((_, i) => String(compileFlowPart('', session, parts, i, mode).body.prompt || '')));
}

/** One clip driven by a reference video (V2V), from the generator card. */
export async function generateReferenceClip(args: {
  orgId: string;
  prompt: string;
  referenceVideoUrl: string;
  /** Optional stills (e.g. room plate) — Seedance/Wan multimodal reference. */
  referenceImageUrls?: string[];
  audioUrl?: string;
  pick: VideoModelPickerValue;
  aspectRatio: FragmentadorSession['aspectRatio'];
  galleryTitle?: string;
  onProgress?: (label: string) => void;
}): Promise<{ clipUrl: string }> {
  args.onProgress?.('Enviando…');
  const refImages = (args.referenceImageUrls || []).filter(Boolean).slice(0, 9);
  const { data, error } = await supabase.functions.invoke('create-video', {
    body: {
      organization_id: args.orgId,
      mode: 'v2v',
      base_video_url: args.referenceVideoUrl,
      prompt: args.prompt,
      video_model_id: args.pick.videoModelId,
      resolution_id: args.pick.resolutionId,
      duration_seconds: args.pick.durationSeconds,
      generate_audio: Boolean(args.pick.generateAudio),
      aspect_ratio: args.aspectRatio,
      gallery_title: args.galleryTitle || undefined,
      ...(args.audioUrl ? { audio_url: args.audioUrl } : {}),
      ...(refImages.length
        ? { reference_images: refImages.map((url) => ({ url, type: 'reference' })) }
        : {}),
    },
  });
  if (error || data?.error) {
    const msg = await edgeErrorMessage(error, data);
    throw new Error(msg);
  }
  if (data?.async && data.job_id) {
    rememberCreationVideoJob(data.job_id);
    args.onProgress?.('Gerando vídeo');
    const job = await waitForJob(data.job_id, {
      timeoutMs: 30 * 60 * 1000,
      onStatus: (j) => {
        if (j.status !== 'completed' && j.status !== 'failed') args.onProgress?.(`Gerando vídeo · ${jobStatusLabel(j)}`);
      },
    });
    const url = toMediaUrl(job.result_url) || '';
    if (!url) throw new Error('Vídeo sem URL de resultado.');
    return { clipUrl: url };
  }
  const url = toMediaUrl(data?.video_url) || '';
  if (!url) throw new Error('Vídeo sem URL de resultado.');
  return { clipUrl: url };
}
